import { Bus, MapPin, Clock, Phone, CheckCircle2 } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { useEvents } from '../../context/EventContext';
import { Card, Badge, StatusBadge, Loader, EmptyState, SectionTitle } from '../../components/UI';
import { fmtDateTime } from '../../utils/format';
import { NotIncluded } from './shared';

export default function AttendeeTransport() {
  const { eventId, hasModule } = useEvents();
  const included = hasModule('buses');
  const { data: buses, loading } = useFetch('/buses/my', { event: eventId }, { skip: !eventId || !included });

  if (!included) return <><SectionTitle title="Transport" /><NotIncluded what="Coach transport" icon={Bus} /></>;
  if (loading) return <Loader full label="Loading your coaches" />;

  return (
    <>
      <SectionTitle title="Transport" subtitle="Your coach, seat and boarding point" />
      {!buses?.length ? (
        <EmptyState message="No coach assigned yet" hint="Your coach and seat appear here once the organiser allocates them." icon={Bus} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {buses.map((b) => (
            <Card key={b._id} padding={20}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <p style={{ fontSize: 17, fontWeight: 700 }}>Bus {b.busNumber}</p>
                <StatusBadge status={b.status} />
              </div>
              {b.tripType && <Badge tone="neutral" style={{ marginTop: 8 }}>{String(b.tripType).replace(/_/g, ' ')}</Badge>}

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 14 }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 34, fontWeight: 700, color: 'var(--gold-300)' }}>{b.mySeat || '—'}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-lo)' }}>your seat</span>
                {b.boarded && <Badge tone="success" style={{ marginLeft: 'auto' }}><CheckCircle2 size={10} /> Boarded</Badge>}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 12, fontSize: 13, color: 'var(--text-mid)' }}>
                {(b.route?.from || b.route?.to) && <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><MapPin size={13} color="var(--gold-500)" />{[b.route?.from, b.route?.to].filter(Boolean).join(' → ')}</span>}
                {b.boardingPoint && <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><MapPin size={13} color="var(--gold-500)" />Board at {b.boardingPoint}</span>}
                {b.departureAt && <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Clock size={13} color="var(--gold-500)" />Departs {fmtDateTime(b.departureAt)}</span>}
                {b.coordinator?.name && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Phone size={13} color="var(--gold-500)" />{b.coordinator.name}
                    {b.coordinator.mobile && <a href={`tel:${b.coordinator.mobile}`} style={{ color: 'var(--gold-300)' }}>· {b.coordinator.mobile}</a>}
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
